import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer,
  Input,
  Select,
  Button,
  Tag,
  Empty,
  Progress,
} from 'antd';
import {
  DashboardOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  CarOutlined,
  CloseCircleOutlined,
  CoffeeOutlined,
  FileTextOutlined,
  WarningOutlined,
  IdcardOutlined,
  InboxOutlined,
  ReloadOutlined,
  UserOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import moment from 'moment-jalaali';
import MainLayout from '../../../layouts/MainLayout';
import { hrDashboardApi } from '../../../utils/api';
import { resolveAvatarSrc } from '../../../utils/avatarUtils';

const JALALI_MONTHS = [
  { value: 1, label: 'فروردین' },
  { value: 2, label: 'اردیبهشت' },
  { value: 3, label: 'خرداد' },
  { value: 4, label: 'تیر' },
  { value: 5, label: 'مرداد' },
  { value: 6, label: 'شهریور' },
  { value: 7, label: 'مهر' },
  { value: 8, label: 'آبان' },
  { value: 9, label: 'آذر' },
  { value: 10, label: 'دی' },
  { value: 11, label: 'بهمن' },
  { value: 12, label: 'اسفند' },
];

const categoryTitles = {
  present: 'لیست پرسنل حاضر امروز',
  late: 'لیست تاخیرهای ورودی امروز',
  leave: 'لیست پرسنل در مرخصی امروز',
  mission: 'لیست پرسنل در مأموریت امروز',
  absent: 'لیست غایبین امروز',
  day_off: 'لیست پرسنل در روز استراحت / تعطیل',
};

const HrDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // فیلتر ماه و سال جلالی
  const currentJYear = useMemo(() => moment().jYear(), []);
  const currentJMonth = useMemo(() => moment().jMonth() + 1, []);
  const [selectedYear, setSelectedYear] = useState(currentJYear);
  const [selectedMonth, setSelectedMonth] = useState(currentJMonth);

  // وضعیت دراور لیست پرسنل امروز
  const [drawerCategory, setDrawerCategory] = useState(null);
  const [drawerSearch, setDrawerSearch] = useState('');

  // تب فعال برای کارتابل تاییدات
  const [activeApprovalTab, setActiveApprovalTab] = useState('all');

  // رفرنس برای تایمر به‌روزرسانی خودکار
  const autoRefreshTimerRef = useRef(null);

  // ساخت لیست سال‌ها
  const yearOptions = useMemo(() => {
    return [
      { value: currentJYear - 1, label: `${currentJYear - 1}` },
      { value: currentJYear, label: `${currentJYear}` },
      { value: currentJYear + 1, label: `${currentJYear + 1}` },
    ];
  }, [currentJYear]);

  // دریافت داده‌های داشبورد
  const fetchDashboardData = useCallback(
    async (isBackground = false) => {
      if (isBackground) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const res = await hrDashboardApi.getSummary({
          year: selectedYear,
          month: selectedMonth,
        });

        if (res?.data) {
          setData(res.data);
        } else if (res?.today) {
          setData(res);
        } else {
          throw new Error('فرمت داده‌های دریافتی نامعتبر است');
        }
      } catch (err) {
        console.error('خطا در دریافت اطلاعات داشبورد منابع انسانی:', err);
        setError('خطا در ارتباط با سرور یا دریافت اطلاعات داشبورد منابع انسانی');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [selectedYear, selectedMonth]
  );

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // به‌روزرسانی خودکار هر ۶۰ ثانیه برای وضعیت زنده
  useEffect(() => {
    autoRefreshTimerRef.current = setInterval(() => {
      fetchDashboardData(true);
    }, 300000);

    return () => {
      if (autoRefreshTimerRef.current) {
        clearInterval(autoRefreshTimerRef.current);
      }
    };
  }, [fetchDashboardData]);

  // لیست پرسنل دراور فیلتر شده بر اساس جستجو
  const activeDrawerList = useMemo(() => {
    if (!drawerCategory || !data?.today?.lists) return [];
    const list = data.today.lists[drawerCategory] || [];
    if (!drawerSearch.trim()) return list;
    const q = drawerSearch.trim().toLowerCase();
    return list.filter(
      (emp) =>
        (emp.name && emp.name.toLowerCase().includes(q)) ||
        (emp.department && emp.department.toLowerCase().includes(q)) ||
        (emp.designation && emp.designation.toLowerCase().includes(q))
    );
  }, [drawerCategory, data?.today?.lists, drawerSearch]);

  const handleOpenDrawer = (category) => {
    setDrawerSearch('');
    setDrawerCategory(category);
  };

  const handleCloseDrawer = () => {
    setDrawerCategory(null);
    setDrawerSearch('');
  };

  // اسکلتون بارگذاری
  if (loading && !data) {
    return (
      <MainLayout>
        <div style={{ fontFamily: 'CustomFont' }} className="space-y-6">
          {/* هدر اسکلتون */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm animate-pulse">
            <div className="h-7 w-64 bg-gray-200 dark:bg-gray-700 rounded-lg mb-2" />
            <div className="h-4 w-96 bg-gray-200 dark:bg-gray-700 rounded-lg" />
          </div>

          {/* کارت‌های وضعیت امروز */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3.5">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="h-28 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 animate-pulse flex flex-col justify-between"
              >
                <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-7 w-12 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
            ))}
          </div>

          {/* آمارهای ماهانه */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="h-32 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 animate-pulse"
              >
                <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded mb-3" />
                <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
            ))}
          </div>

          {/* هشدارها */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="h-72 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 animate-pulse"
              />
            ))}
          </div>
        </div>
      </MainLayout>
    );
  }

  // وضعیت خطا
  if (error && !data) {
    return (
      <MainLayout>
        <div style={{ fontFamily: 'CustomFont' }} className="p-8">
          <div className="bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/50 rounded-2xl p-8 text-center max-w-lg mx-auto">
            <WarningOutlined className="text-rose-500 text-4xl mb-3" />
            <h3 className="text-lg font-bold text-rose-800 dark:text-rose-300 m-0">خطا در بارگذاری داشبورد</h3>
            <p className="text-sm text-rose-600 dark:text-rose-400 mt-2 mb-4">{error}</p>
            <Button
              type="primary"
              danger
              icon={<ReloadOutlined />}
              onClick={() => fetchDashboardData()}
              className="rounded-lg shadow-sm"
              style={{ fontFamily: 'CustomFont' }}
            >
              تلاش مجدد
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  const { today, month, alerts } = data || {};

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }} className="space-y-6">
        {/* ۱. هدر اصلی صفحه */}
        <div className="bg-white dark:bg-gray-800 p-5 md:p-6 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm transition-all">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex items-center gap-3.5">
              <div className="w-12 h-12 rounded-2xl bg-cyan-50 dark:bg-cyan-900/30 border border-cyan-200 dark:border-cyan-800/50 flex items-center justify-center text-cyan-600 dark:text-cyan-400 text-2xl flex-shrink-0 shadow-sm">
                <DashboardOutlined />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-black text-gray-800 dark:text-gray-100 m-0 tracking-tight">
                  داشبورد مدیریتی منابع انسانی
                </h1>
                <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-1 mb-0 flex items-center flex-wrap gap-2">
                  <span>
                    امروز: <strong className="text-gray-700 dark:text-gray-200">{today?.jalali_date}</strong> ({today?.date})
                  </span>
                  <span>•</span>
                  <span>
                    کل پرسنل فعال سازمان:{' '}
                    <strong className="text-cyan-600 dark:text-cyan-400">{today?.total_employees || 0}</strong> نفر
                  </span>
                </p>
              </div>
            </div>

            <div className="flex items-center flex-wrap gap-2.5 self-end md:self-auto">
              {alerts?.total_alerts_count > 0 && (
                <div className="flex items-center gap-2 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800/60 text-rose-700 dark:text-rose-300 px-3.5 py-1.5 rounded-full text-xs font-bold animate-pulse shadow-xs">
                  <span className="w-2.5 h-2.5 rounded-full bg-rose-500" />
                  <span>{alerts.total_alerts_count} مورد نیازمند اقدام فوری</span>
                </div>
              )}

              <Button
                icon={<ReloadOutlined spin={refreshing} />}
                onClick={() => fetchDashboardData(true)}
                disabled={refreshing}
                size="middle"
                className="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200 border-0 rounded-xl font-semibold shadow-xs"
                style={{ fontFamily: 'CustomFont' }}
              >
                به‌روزرسانی
              </Button>
            </div>
          </div>
        </div>

        {/* ۲. وضعیت زنده امروز (Today's Live Snapshot) */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-base font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2 m-0">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
              وضعیت زنده پرسنل در امروز
            </h2>
            <span className="text-xs text-gray-400 dark:text-gray-500">
              با کلیک روی هر کارت، لیست افراد باز می‌شود
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3.5">
            {/* حاضرین */}
            <TodayStatusCard
              title="حاضرین"
              count={today?.counts?.present || 0}
              icon={<CheckCircleOutlined className="text-emerald-600 dark:text-emerald-400 text-lg" />}
              cardBg="bg-emerald-50/80 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800/40 hover:border-emerald-400"
              badgeBg="bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200"
              onClick={() => handleOpenDrawer('present')}
            />

            {/* تاخیر ورودی */}
            <TodayStatusCard
              title="تاخیر ورودی"
              count={today?.counts?.late || 0}
              icon={<ClockCircleOutlined className="text-amber-600 dark:text-amber-400 text-lg" />}
              cardBg="bg-amber-50/80 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800/40 hover:border-amber-400"
              badgeBg="bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200"
              onClick={() => handleOpenDrawer('late')}
            />

            {/* مرخصی */}
            <TodayStatusCard
              title="مرخصی"
              count={today?.counts?.leave || 0}
              icon={<CalendarOutlined className="text-sky-600 dark:text-sky-400 text-lg" />}
              cardBg="bg-sky-50/80 dark:bg-sky-950/20 border-sky-200 dark:border-sky-800/40 hover:border-sky-400"
              badgeBg="bg-sky-100 dark:bg-sky-900/40 text-sky-800 dark:text-sky-200"
              onClick={() => handleOpenDrawer('leave')}
            />

            {/* مأموریت */}
            <TodayStatusCard
              title="مأموریت"
              count={today?.counts?.mission || 0}
              icon={<CarOutlined className="text-indigo-600 dark:text-indigo-400 text-lg" />}
              cardBg="bg-indigo-50/80 dark:bg-indigo-950/20 border-indigo-200 dark:border-indigo-800/40 hover:border-indigo-400"
              badgeBg="bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-200"
              onClick={() => handleOpenDrawer('mission')}
            />

            {/* غایبین */}
            <TodayStatusCard
              title="غایبین"
              count={today?.counts?.absent || 0}
              icon={<CloseCircleOutlined className="text-rose-600 dark:text-rose-400 text-lg" />}
              cardBg="bg-rose-50/80 dark:bg-rose-950/20 border-rose-200 dark:border-rose-800/40 hover:border-rose-400"
              badgeBg="bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-200"
              onClick={() => handleOpenDrawer('absent')}
            />

            {/* روز استراحت */}
            <TodayStatusCard
              title="روز استراحت"
              count={today?.counts?.day_off || 0}
              icon={<CoffeeOutlined className="text-slate-600 dark:text-slate-400 text-lg" />}
              cardBg="bg-slate-100/90 dark:bg-slate-900/40 border-slate-200 dark:border-slate-700 hover:border-slate-400"
              badgeBg="bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300"
              onClick={() => handleOpenDrawer('day_off')}
            />
          </div>
        </div>

        {/* ۳. کارکرد تجمیعی ماه جاری شمسی (This Month Metrics) */}
        <div className="bg-white dark:bg-gray-800 p-5 md:p-6 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm space-y-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b border-gray-100 dark:border-gray-700/60 pb-4">
            <div>
              <h2 className="text-base font-bold text-gray-800 dark:text-gray-100 m-0">
                کارکرد ماه {month?.month_name} سال {month?.jalali_year}
              </h2>
              <span className="text-xs text-gray-400 dark:text-gray-500 mt-1 block">
                بازه آماری: از {month?.start_date} تا {month?.end_date}
              </span>
            </div>

            {/* سلکتور تغییر سال و ماه */}
            <div className="flex items-center gap-2">
              <Select
                value={selectedYear}
                onChange={setSelectedYear}
                options={yearOptions}
                size="middle"
                className="w-24 custom-select"
                style={{ fontFamily: 'CustomFont' }}
              />
              <Select
                value={selectedMonth}
                onChange={setSelectedMonth}
                options={JALALI_MONTHS}
                size="middle"
                className="w-32 custom-select"
                style={{ fontFamily: 'CustomFont' }}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* اضافه‌کاری مصوب */}
            <MetricBox
              title="اضافه‌کاری مصوب HR"
              value={month?.overtime?.formatted || '۰۰:۰۰'}
              unit="ساعت"
              subText={`${month?.overtime?.approved_requests_count || 0} درخواست تأییدشده`}
              accentColor="text-indigo-600 dark:text-indigo-400"
              bgBadge="bg-indigo-50 dark:bg-indigo-950/30"
            />

            {/* کسری کار سازمانی */}
            <MetricBox
              title="کسری کار سازمانی"
              value={month?.deficit?.formatted || '۰۰:۰۰'}
              unit="ساعت"
              subText={`${month?.deficit?.minutes || 0} دقیقه کل در این ماه`}
              accentColor="text-rose-600 dark:text-rose-400"
              bgBadge="bg-rose-50 dark:bg-rose-950/30"
            />

            {/* مرخصی مصرف‌شده */}
            <MetricBox
              title="مرخصی مصرف‌شده"
              value={month?.leaves_used?.total_days || 0}
              unit="روز"
              subText={
                month?.leaves_used?.by_type && Object.keys(month.leaves_used.by_type).length > 0
                  ? Object.entries(month.leaves_used.by_type)
                    .map(([k, v]) => `${k}: ${v}`)
                    .join(' | ')
                  : 'بدون مرخصی ثبت‌شده'
              }
              accentColor="text-sky-600 dark:text-sky-400"
              bgBadge="bg-sky-50 dark:bg-sky-950/30"
            />

            {/* نرخ انضباط و حضور */}
            <div className="p-4 bg-gray-50/70 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/60 rounded-2xl flex flex-col justify-between">
              <div>
                <span className="text-xs font-bold text-gray-500 dark:text-gray-400">نرخ انضباط و حضور</span>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-2xl font-black text-emerald-600 dark:text-emerald-400">
                    {month?.attendance_rate_percentage || 0}٪
                  </span>
                  <span className="text-xs text-gray-400">درصد</span>
                </div>
              </div>
              <div className="mt-3">
                <Progress
                  percent={month?.attendance_rate_percentage || 0}
                  showInfo={false}
                  strokeColor="#10B981"
                  trailColor="rgba(156, 163, 175, 0.2)"
                  size={['100%', 6]}
                />
                <span className="text-[11px] text-gray-400 block mt-1.5">نسبت ساعات موثر به کل موظفی</span>
              </div>
            </div>
          </div>
        </div>

        {/* ۴. هشدارهای هوشمند و اقدام سریع (Smart Actionable HR Alerts) */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-base font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2 m-0">
              <WarningOutlined className="text-amber-500 text-lg" />
              هشدارهای هوشمند و موارد نیازمند اقدام فوری
            </h2>
            <span className="text-xs text-gray-400 dark:text-gray-500">
              مجموع: {alerts?.total_alerts_count || 0} هشدار
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* ۱. قراردادهای در آستانه پایان */}
            <AlertCardContainer
              title="قراردادهای در آستانه پایان"
              count={alerts?.counts?.expiring_contracts || 0}
              badgeColor="bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200"
              icon={<FileTextOutlined className="text-amber-600 dark:text-amber-400 text-base" />}
            >
              {(alerts?.expiring_contracts || []).map((item) => (
                <div
                  key={item.user_id}
                  className="p-3 rounded-xl border border-gray-100 dark:border-gray-700/60 bg-gray-50/70 dark:bg-gray-900/30 flex justify-between items-center gap-2 shadow-2xs"
                >
                  <div className="min-w-0">
                    <div className="font-bold text-xs text-gray-800 dark:text-gray-100 truncate">{item.name}</div>
                    <div className="text-[11px] text-gray-400 truncate mt-0.5">
                      {item.department} • {item.designation}
                    </div>
                  </div>
                  <Tag
                    color={item.is_expired ? 'error' : 'warning'}
                    className="text-[10px] font-bold m-0 border-0 rounded-md"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    {item.status_text}
                  </Tag>
                </div>
              ))}
            </AlertCardContainer>

            {/* ۲. دوره‌های آزمایشی رو به پایان */}
            <AlertCardContainer
              title="دوره‌های آزمایشی رو به پایان"
              count={alerts?.counts?.expiring_probations || 0}
              badgeColor="bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-200"
              icon={<IdcardOutlined className="text-purple-600 dark:text-purple-400 text-base" />}
            >
              {(alerts?.expiring_probations || []).map((item) => (
                <div
                  key={item.user_id}
                  className="p-3 rounded-xl border border-gray-100 dark:border-gray-700/60 bg-gray-50/70 dark:bg-gray-900/30 flex justify-between items-center gap-2 shadow-2xs"
                >
                  <div className="min-w-0">
                    <div className="font-bold text-xs text-gray-800 dark:text-gray-100 truncate">{item.name}</div>
                    <div className="text-[11px] text-gray-400 truncate mt-0.5">{item.department}</div>
                  </div>
                  <Tag
                    color="purple"
                    className="text-[10px] font-bold m-0 border-0 rounded-md"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    {item.status_text}
                  </Tag>
                </div>
              ))}
            </AlertCardContainer>

            {/* ۳. ترددهای ناقص (ورود بدون خروج) */}
            <AlertCardContainer
              title="ترددهای ناقص (ورود بدون خروج)"
              count={alerts?.counts?.missing_clock_outs || 0}
              badgeColor="bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-200"
              icon={<WarningOutlined className="text-rose-600 dark:text-rose-400 text-base" />}
            >
              {(alerts?.missing_clock_outs || []).map((item) => (
                <div
                  key={item.attendance_id}
                  className="p-3 rounded-xl border border-gray-100 dark:border-gray-700/60 bg-gray-50/70 dark:bg-gray-900/30 flex justify-between items-center gap-2 shadow-2xs"
                >
                  <div className="min-w-0">
                    <div className="font-bold text-xs text-gray-800 dark:text-gray-100 truncate">{item.name}</div>
                    <div className="text-[11px] text-gray-400 truncate mt-0.5">تاریخ: {item.jalali_date}</div>
                  </div>
                  <Tag
                    color="error"
                    className="text-[10px] font-mono font-bold m-0 border-0 rounded-md"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    ورود: {item.clock_in_time}
                  </Tag>
                </div>
              ))}
            </AlertCardContainer>

            {/* ۴. کارتابل تاییدات معلق */}
            <AlertCardContainer
              title="کارتابل تاییدات معلق"
              count={alerts?.counts?.pending_approvals || 0}
              badgeColor="bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-200"
              icon={<InboxOutlined className="text-indigo-600 dark:text-indigo-400 text-base" />}
            >
              {/* فیلتر نوع تاییدات */}
              <div className="flex items-center gap-1 mb-2 bg-gray-100 dark:bg-gray-700/50 p-1 rounded-lg text-[11px]">
                <button
                  type="button"
                  onClick={() => setActiveApprovalTab('all')}
                  className={`flex-1 py-1 rounded text-center transition ${activeApprovalTab === 'all'
                      ? 'bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 font-bold shadow-xs'
                      : 'text-gray-500 hover:text-gray-800 dark:text-gray-400'
                    }`}
                >
                  همه
                </button>
                <button
                  type="button"
                  onClick={() => setActiveApprovalTab('leaves')}
                  className={`flex-1 py-1 rounded text-center transition ${activeApprovalTab === 'leaves'
                      ? 'bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 font-bold shadow-xs'
                      : 'text-gray-500 hover:text-gray-800 dark:text-gray-400'
                    }`}
                >
                  مرخصی ({alerts?.counts?.pending_leaves || 0})
                </button>
                <button
                  type="button"
                  onClick={() => setActiveApprovalTab('overtime')}
                  className={`flex-1 py-1 rounded text-center transition ${activeApprovalTab === 'overtime'
                      ? 'bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 font-bold shadow-xs'
                      : 'text-gray-500 hover:text-gray-800 dark:text-gray-400'
                    }`}
                >
                  اضافه‌کاری ({alerts?.counts?.pending_overtime || 0})
                </button>
              </div>

              {/* مرخصی‌های معلق */}
              {(activeApprovalTab === 'all' || activeApprovalTab === 'leaves') &&
                (alerts?.pending_approvals?.leaves || []).map((l) => (
                  <div
                    key={`leave-${l.id}`}
                    className="p-2.5 rounded-lg border border-sky-100 dark:border-sky-900/40 bg-sky-50/60 dark:bg-sky-950/20 text-xs flex justify-between items-center gap-2 mb-1.5"
                  >
                    <span className="truncate">
                      مرخصی {l.leave_type}: <strong className="text-gray-800 dark:text-gray-100">{l.name}</strong>
                    </span>
                    <span className="text-[10px] text-sky-700 dark:text-sky-300 flex-shrink-0">{l.jalali_date}</span>
                  </div>
                ))}

              {/* اضافه‌کاری‌های معلق */}
              {(activeApprovalTab === 'all' || activeApprovalTab === 'overtime') &&
                (alerts?.pending_approvals?.overtime || []).map((o) => (
                  <div
                    key={`ot-${o.id}`}
                    className="p-2.5 rounded-lg border border-indigo-100 dark:border-indigo-900/40 bg-indigo-50/60 dark:bg-indigo-950/20 text-xs flex justify-between items-center gap-2 mb-1.5"
                  >
                    <span className="truncate">
                      اضافه‌کاری: <strong className="text-gray-800 dark:text-gray-100">{o.name}</strong> ({o.requested_time})
                    </span>
                    <span className="text-[10px] text-indigo-700 dark:text-indigo-300 flex-shrink-0">
                      {o.jalali_date}
                    </span>
                  </div>
                ))}

              {/* مأموریت‌های معلق */}
              {(activeApprovalTab === 'all' || activeApprovalTab === 'missions') &&
                (alerts?.pending_approvals?.missions || []).map((m) => (
                  <div
                    key={`m-${m.id}`}
                    className="p-2.5 rounded-lg border border-purple-100 dark:border-purple-900/40 bg-purple-50/60 dark:bg-purple-950/20 text-xs flex justify-between items-center gap-2 mb-1.5"
                  >
                    <span className="truncate">
                      مأموریت {m.destination}: <strong className="text-gray-800 dark:text-gray-100">{m.name}</strong>
                    </span>
                    <span className="text-[10px] text-purple-700 dark:text-purple-300 flex-shrink-0">{m.start_date}</span>
                  </div>
                ))}
            </AlertCardContainer>
          </div>
        </div>

        {/* ۵. دراور جزئیات پرسنل (Right Drawer) */}
        <Drawer
          title={
            <div className="flex items-center justify-between" style={{ fontFamily: 'CustomFont' }}>
              <div>
                <span className="font-bold text-base text-gray-800 dark:text-gray-100 block">
                  {categoryTitles[drawerCategory] || 'لیست پرسنل'}
                </span>
                <span className="text-xs text-gray-400 mt-0.5 block">تعداد: {activeDrawerList.length} نفر</span>
              </div>
            </div>
          }
          placement="right"
          onClose={handleCloseDrawer}
          open={!!drawerCategory}
          width={420}
          className="custom-drawer"
          styles={{
            body: {
              padding: '16px',
              fontFamily: 'CustomFont',
            },
          }}
        >
          {/* باکس جستجو در دراور */}
          <div className="mb-4">
            <Input
              prefix={<SearchOutlined className="text-gray-400" />}
              placeholder="جستجو بر اساس نام، دپارتمان یا سمت..."
              value={drawerSearch}
              onChange={(e) => setDrawerSearch(e.target.value)}
              allowClear
              className="rounded-xl"
              style={{ fontFamily: 'CustomFont' }}
            />
          </div>

          {/* لیست پرسنل */}
          <div className="space-y-2.5">
            {activeDrawerList.length === 0 ? (
              <div className="py-16 text-center">
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <span className="text-gray-400 text-sm" style={{ fontFamily: 'CustomFont' }}>
                      هیچ پرسنلی در این وضعیت یافت نشد
                    </span>
                  }
                />
              </div>
            ) : (
              activeDrawerList.map((emp) => {
                const avatarSrc = resolveAvatarSrc(emp.avatar);
                return (
                  <div
                    key={emp.id}
                    onClick={() => {
                      handleCloseDrawer();
                      navigate(`/hr/employees/${emp.id}`);
                    }}
                    className="p-3 rounded-xl border border-gray-100 dark:border-gray-700/70 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 shadow-2xs hover:shadow-xs transition flex items-center justify-between gap-3 cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="relative flex-shrink-0">
                        {avatarSrc ? (
                          <img
                            src={avatarSrc}
                            alt={emp.name}
                            className="w-10 h-10 rounded-full object-cover ring-2 ring-gray-100 dark:ring-gray-700"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 flex items-center justify-center font-bold text-sm">
                            {emp.name ? emp.name.charAt(0) : <UserOutlined />}
                          </div>
                        )}
                      </div>

                      <div className="min-w-0">
                        <div className="font-bold text-sm text-gray-800 dark:text-gray-100 truncate">{emp.name}</div>
                        <div className="text-xs text-gray-400 truncate mt-0.5">
                          {emp.department || 'بدون دپارتمان'} {emp.designation ? `• ${emp.designation}` : ''}
                        </div>
                      </div>
                    </div>

                    {/* اطلاعات تکمیلی براساس نوع کارت */}
                    {emp.clock_in_time && (
                      <Tag
                        color={emp.is_late ? 'warning' : 'success'}
                        className="text-xs font-mono font-bold m-0 border-0 rounded-md px-2 py-0.5 flex-shrink-0"
                        style={{ fontFamily: 'CustomFont' }}
                      >
                        {emp.clock_in_time}
                      </Tag>
                    )}

                    {emp.leave_type && (
                      <Tag
                        color="processing"
                        className="text-xs font-bold m-0 border-0 rounded-md px-2 py-0.5 flex-shrink-0"
                        style={{ fontFamily: 'CustomFont' }}
                      >
                        {emp.leave_type}
                      </Tag>
                    )}

                    {emp.destination && (
                      <Tag
                        color="purple"
                        className="text-xs font-bold m-0 border-0 rounded-md px-2 py-0.5 flex-shrink-0"
                        style={{ fontFamily: 'CustomFont' }}
                      >
                        {emp.destination}
                      </Tag>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </Drawer>
      </div>
    </MainLayout>
  );
};

// کامپوننت کارت وضعیت زنده امروز
const TodayStatusCard = ({ title, count, icon, cardBg, badgeBg, onClick }) => (
  <div
    onClick={onClick}
    className={`p-4 rounded-2xl border ${cardBg} transition-all duration-200 cursor-pointer shadow-2xs hover:shadow-md hover:-translate-y-0.5 flex flex-col justify-between`}
  >
    <div className="flex justify-between items-center">
      <span className="text-xs font-bold text-gray-700 dark:text-gray-300">{title}</span>
      {icon}
    </div>
    <div className="flex items-baseline justify-between mt-3">
      <span className="text-2xl font-black text-gray-900 dark:text-white tracking-tight">{count}</span>
      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${badgeBg}`}>مشاهده</span>
    </div>
  </div>
);

// کامپوننت جعبه شاخص ماهانه
const MetricBox = ({ title, value, unit, subText, accentColor, bgBadge }) => (
  <div className="p-4 bg-gray-50/70 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/60 rounded-2xl flex flex-col justify-between">
    <div>
      <span className="text-xs font-bold text-gray-500 dark:text-gray-400">{title}</span>
      <div className="mt-2 flex items-baseline gap-1">
        <span className={`text-2xl font-black tracking-tight ${accentColor}`}>{value}</span>
        <span className="text-xs text-gray-400">{unit}</span>
      </div>
    </div>
    <div className="mt-3 text-[11px] text-gray-400 border-t border-gray-200/50 dark:border-gray-700/50 pt-2 truncate">
      {subText}
    </div>
  </div>
);

// کامپوننت کانتینر هشدارهای هوشمند
const AlertCardContainer = ({ title, count, badgeColor, icon, children }) => (
  <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-sm flex flex-col h-80">
    <div className="flex justify-between items-center border-b border-gray-100 dark:border-gray-700 pb-3 mb-3">
      <div className="flex items-center gap-2 min-w-0">
        {icon}
        <span className="text-xs font-bold text-gray-800 dark:text-gray-200 truncate">{title}</span>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${badgeColor}`}>{count} مورد</span>
      </div>
    </div>

    <div className="flex-1 overflow-y-auto custom-scrollbar space-y-2 pr-0.5">
      {count === 0 ? (
        <div className="h-full flex flex-col items-center justify-center text-xs text-gray-400 dark:text-gray-500 gap-1.5 py-8">
          <CheckCircleOutlined className="text-emerald-500 text-xl" />
          <span>موردی برای اقدام وجود ندارد ✨</span>
        </div>
      ) : (
        children
      )}
    </div>
  </div>
);

export default HrDashboard;
